import { useQuery } from "@tanstack/react-query";
import { formatBRL } from "@yatta/domain";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import {
  assetContracts as mockAssetContracts,
  assetCustomers as mockAssetCustomers,
  assets as mockAssets,
  assetReceivables as mockReceivables,
  propertyLeases as mockPropertyLeases,
  vehicleRentals as mockVehicleRentals,
  vehicleSales as mockVehicleSales,
} from "./assets-mock-data";

type ViewValue = string | number | boolean | null;

async function queryView<T extends Record<string, ViewValue>>(view: string): Promise<T[]> {
  const { data, error } = await supabase.from(view).select("*");
  if (error) throw error;
  return (data ?? []) as T[];
}

export function useAssets() {
  return useQuery({
    queryKey: ["assets"],
    queryFn: async () => {
      if (env.useMocks) return mockAssets;
      const rows = await queryView<Record<string, ViewValue>>("app_asset_directory");
      return rows.map((row) => ({
        id: String(row.id),
        code: String(row.code),
        type: String(row.asset_type),
        name: String(row.name),
        description: String(row.description ?? "—"),
        status: String(row.status),
        value:
          row.current_value_cents == null
            ? "—"
            : formatBRL(BigInt(String(row.current_value_cents))),
      }));
    },
  });
}

export function useVehicleSales() {
  return useQuery({
    queryKey: ["vehicle-sales"],
    queryFn: async () => {
      if (env.useMocks) return mockVehicleSales;
      const rows = await queryView<Record<string, ViewValue>>("app_vehicle_sale_directory");
      return rows.map((row) => ({
        id: String(row.id),
        number: String(row.number),
        customer: String(row.customer),
        vehicle: String(row.vehicle),
        mode: String(row.sale_mode),
        total: formatBRL(BigInt(String(row.sale_price_cents ?? 0))),
        downPayment: formatBRL(BigInt(String(row.down_payment_cents ?? 0))),
        installments: String(row.installment_count ?? 1),
        status: String(row.status),
        transfer: String(row.transfer_status),
      }));
    },
  });
}

export function usePropertyLeases() {
  return useQuery({
    queryKey: ["property-leases"],
    queryFn: async () => {
      if (env.useMocks) return mockPropertyLeases;
      const rows = await queryView<Record<string, ViewValue>>("app_property_lease_directory");
      return rows.map((row) => ({
        id: String(row.id),
        number: String(row.number),
        tenant: String(row.tenant),
        property: String(row.property),
        monthlyRent: formatBRL(BigInt(String(row.monthly_rent_cents ?? 0))),
        dueDay: String(row.due_day ?? "—"),
        startsAt: row.starts_at ? new Date(String(row.starts_at)).toLocaleDateString("pt-BR") : "—",
        endsAt: row.ends_at
          ? new Date(String(row.ends_at)).toLocaleDateString("pt-BR")
          : "Indeterminado",
        status: String(row.status),
      }));
    },
  });
}

export function useVehicleRentals() {
  return useQuery({
    queryKey: ["vehicle-rentals"],
    queryFn: async () => {
      if (env.useMocks) return mockVehicleRentals;
      const rows = await queryView<Record<string, ViewValue>>("app_vehicle_rental_directory");
      return rows.map((row) => ({
        id: String(row.id),
        number: String(row.number),
        driver: String(row.driver),
        vehicle: String(row.vehicle),
        frequency: String(row.rental_frequency),
        rent: formatBRL(BigInt(String(row.rental_amount_cents ?? 0))),
        dueDay: String(row.due_day ?? "—"),
        mileage: "—",
        status: String(row.status),
      }));
    },
  });
}

export function useAssetReceivables() {
  return useQuery({
    queryKey: ["asset-receivables"],
    queryFn: async () => {
      if (env.useMocks) return mockReceivables;
      const rows = await queryView<Record<string, ViewValue>>("app_receivable_directory");
      return rows.map((row) => ({
        id: String(row.id),
        customer: String(row.customer),
        agreement: String(row.agreement_number),
        asset: String(row.asset),
        sequence: String(row.sequence),
        dueDate: new Date(String(row.due_date)).toLocaleDateString("pt-BR"),
        original: formatBRL(BigInt(String(row.original_amount_cents ?? 0))),
        updated: formatBRL(BigInt(String(row.updated_amount_cents ?? 0))),
        daysLate: Number(row.days_late ?? 0),
        status: String(row.status),
      }));
    },
  });
}

export function useAssetContracts() {
  return useQuery({
    queryKey: ["asset-contracts"],
    queryFn: async () => {
      if (env.useMocks) return mockAssetContracts;
      const rows = await queryView<Record<string, ViewValue>>("app_asset_contract_directory");
      return rows.map((row) => ({
        id: String(row.id),
        number: String(row.number),
        customer: String(row.customer),
        type: String(row.type),
        asset: String(row.asset),
        status: String(row.status),
        version: String(row.version ?? "v1"),
        updatedAt: new Date(String(row.updated_at)).toLocaleDateString("pt-BR"),
      }));
    },
  });
}

export function useAssetCustomers() {
  return useQuery({
    queryKey: ["asset-customers"],
    queryFn: async () => {
      if (env.useMocks) return mockAssetCustomers;
      const rows = await queryView<Record<string, ViewValue>>("app_counterparty_directory");
      return rows.map((row) => ({
        id: String(row.id),
        name: String(row.display_name),
        documentType: String(row.identifier_type),
        document: String(row.identifier_masked ?? `***${String(row.identifier_last4 ?? "")}`),
        contact: String(row.billing_contact_masked ?? row.billing_email_masked ?? "—"),
        activeAgreements: Number(row.active_agreements ?? 0),
        relationship: String(row.relationship_summary ?? "Cliente"),
        openBalance: formatBRL(BigInt(String(row.open_balance_cents ?? 0))),
        status: String(row.financial_status ?? row.status ?? "ACTIVE"),
      }));
    },
  });
}

export function useAssetDashboard() {
  return useQuery({
    queryKey: ["asset-dashboard"],
    queryFn: async () => {
      if (env.useMocks) {
        const [{ assetDashboardMetrics, vehicleSales, assetReceivables }] = await Promise.all([
          import("./assets-mock-data"),
        ]);
        return {
          metrics: assetDashboardMetrics,
          sales: vehicleSales,
          receivables: assetReceivables,
        };
      }

      const [summaryRows, salesRows, receivableRows] = await Promise.all([
        queryView<Record<string, ViewValue>>("app_assets_dashboard_summary"),
        queryView<Record<string, ViewValue>>("app_vehicle_sale_directory"),
        queryView<Record<string, ViewValue>>("app_receivable_directory"),
      ]);
      const summary = summaryRows[0] ?? {};
      const sales = salesRows.slice(0, 5).map((row) => ({
        id: String(row.id),
        number: String(row.number),
        customer: String(row.customer),
        vehicle: String(row.vehicle),
        mode: String(row.sale_mode),
        total: formatBRL(BigInt(String(row.sale_price_cents ?? 0))),
        downPayment: formatBRL(BigInt(String(row.down_payment_cents ?? 0))),
        installments: String(row.installment_count ?? 1),
        status: String(row.status),
        transfer: String(row.transfer_status),
      }));
      const receivables = receivableRows.map((row) => ({
        id: String(row.id),
        customer: String(row.customer),
        agreement: String(row.agreement_number),
        asset: String(row.asset),
        sequence: String(row.sequence),
        dueDate: new Date(String(row.due_date)).toLocaleDateString("pt-BR"),
        original: formatBRL(BigInt(String(row.original_amount_cents ?? 0))),
        updated: formatBRL(BigInt(String(row.updated_amount_cents ?? 0))),
        daysLate: Number(row.days_late ?? 0),
        status: String(row.status),
      }));

      return {
        metrics: [
          {
            label: "Veículos vendidos",
            value: String(summary.vehicle_sales_total ?? 0),
            delta: "Contratos registrados",
          },
          {
            label: "Imóveis locados",
            value: String(summary.property_leases_active ?? 0),
            delta: "Locações ativas",
          },
          {
            label: "Veículos locados",
            value: String(summary.vehicle_rentals_active ?? 0),
            delta: "Motoristas ativos",
          },
          {
            label: "Recebíveis em aberto",
            value: formatBRL(BigInt(String(summary.open_receivables_cents ?? 0))),
            delta: `${String(summary.overdue_installments ?? 0)} parcela(s) vencida(s)`,
          },
          {
            label: "Em atraso",
            value: formatBRL(BigInt(String(summary.overdue_receivables_cents ?? 0))),
            delta: "Atualizado na data atual",
          },
          {
            label: "Patrimônio estimado",
            value: formatBRL(BigInt(String(summary.portfolio_value_cents ?? 0))),
            delta: "Valor cadastrado dos ativos",
          },
        ],
        sales,
        receivables,
      };
    },
  });
}
